import { useEffect, useMemo } from "react";

import { Guid } from "@/Common/Models/Guid";
import { defaultConfiguration } from "@/Flowsheets/Models/CareElements/DefaultCareComponentConfig";
import { CreateFlowsheetTemplateScope } from "../Api/Contracts/CreateFlowsheetTemplate";
import { ConfigureFlowsheetModalStepEnum } from "../Models/CareElements/ConfigureFlowsheetModalStepEnum";
import { FlowsheetConfig } from "../Models/CareElements/FlowsheetConfig";
import { FlowsheetTemplateScope } from "../Models/FlowsheetTemplate";
import { IUseConfigureSliceResultActions, IUseConfigureSliceState, useConfigureSlice } from "./useConfigureSlice";
import { useCreateFlowsheetTemplate } from "./useCreateFlowsheetTemplate";
import { useFlowsheetTemplate } from "./useFlowsheetTemplate";
import { useGetFlowsheetName } from "./useGetFlowsheetName";
import useGetFlowsheetTemplateNameExists from "./useGetFlowsheetTemplateNameExists";

/**
 * Interface representing the result of configuring a flowsheet.
 * This interface provides the configuration for care components and
 * callback methods to handle user interactions such as linking, adding, or removing care elements.
 */
interface IConfigureFlowsheetResult {
    /**
     * Represents the state of the flowsheet configuration.
     */
    state: IUseConfigureSliceState & {
        /**
         * Indicates whether the template name is a duplicate.
         */
        isTemplateNameDuplicate: boolean | null;

        /**
         * Indicates whether the save button is disabled.
         */
        isSavingDisabled: boolean;

        /**
         * Indicates whether the template name control is disabled.
         */
        isTemplateNameControlDisabled: boolean;

        /**
         * Indicates whether the care element configuration is invalid.
         */
        hasInvalidCareElementConfiguration: boolean;

        /**
         * Represents the set of invalid care elements in the configuration.
         */
        invalidConfigurationCareElements: ReadonlySet<string>;
    };

    /**
     * Represents the actions that can be performed in the flowsheet configuration.
     */
    actions: IUseConfigureSliceResultActions & {
        /**
         * Callback function triggered when the cancel button is clicked.
         */
        onCancel: () => void;

        /**
         * Callback function triggered when the save button is clicked.
         */
        onSave: ({ onApplySavedFlowsheetTemplateToCurrentFlowsheet }: { onApplySavedFlowsheetTemplateToCurrentFlowsheet: () => Promise<void> }) => Promise<void>;
    };
}

function useConfigureFlowsheet(currentTemplateId: Guid, flowsheetId: Guid, patientId: Guid, onClose: () => void): IConfigureFlowsheetResult {
    const { getName } = useGetFlowsheetName();
    const { data: currentFlowsheetTemplate } = useFlowsheetTemplate(currentTemplateId);

    const [state, actions] = useConfigureSlice({
        configurationStep: ConfigureFlowsheetModalStepEnum.Configuration,
        careComponentsConfig: null as FlowsheetConfig,
        isApplyChangesChecked: true,
        templateName: "",
        templateScope: null,
        currentTemplateName: "",
        currentTemplateScope: null as FlowsheetTemplateScope,
        isLoadingCreation: false,
        invalidCareElements: {}
    });

    const hasInvalidCareElementConfiguration = useMemo(
        () => Object.keys(state.invalidCareElements ?? {}).length > 0,
        [state.invalidCareElements]
    );

    const invalidSet = useMemo(
        () => new Set(Object.keys(state.invalidCareElements ?? {})),
        [state.invalidCareElements]
    );

    const {
        createFlowsheetTemplate
    } = useCreateFlowsheetTemplate();

    useEffect(() => {
        if (currentFlowsheetTemplate) {
            const currentFlowsheetCareComponents = currentFlowsheetTemplate.Configuration?.CareComponents ?? [];

            actions.setCareComponentsConfig({
                CareComponents: [
                    ...defaultConfiguration.CareComponents.map(comp => ({
                        ...comp,
                        CareElementsConfig: [
                            ...currentFlowsheetCareComponents.find(p => p.Name === comp.Name)?.CareElementsConfig ?? []
                        ],
                        IsActive: currentFlowsheetCareComponents.some(p => p.Name === comp.Name),
                        Expanded: currentFlowsheetCareComponents.some(p => p.Name === comp.Name)
                    }))
                ]
            });

            actions.setCurrentTemplateName(getName(currentFlowsheetTemplate));
            actions.setCurrentTemplateScope(currentFlowsheetTemplate.TemplateScope);
        }
    }, [currentFlowsheetTemplate]);

    function onCancel(): void {
        switch (state.configurationStep) {
            case ConfigureFlowsheetModalStepEnum.Configuration:
                onClose();
                break;
            case ConfigureFlowsheetModalStepEnum.TemplateType:
                actions.setConfigurationStep(ConfigureFlowsheetModalStepEnum.Configuration);
        }
    }

    async function onSave({ onApplySavedFlowsheetTemplateToCurrentFlowsheet }: { onApplySavedFlowsheetTemplateToCurrentFlowsheet: () => Promise<void> }): Promise<void> {
        switch (state.configurationStep) {
            case ConfigureFlowsheetModalStepEnum.Configuration:
                actions.setConfigurationStep(ConfigureFlowsheetModalStepEnum.TemplateType);
                break;
            case ConfigureFlowsheetModalStepEnum.TemplateType:
                actions.setIsLoadingCreation(true);
                await createFlowsheetTemplate({
                    name: state.templateName,
                    flowsheetId: state.isApplyChangesChecked ? flowsheetId : null,
                    patientId: state.templateScope === CreateFlowsheetTemplateScope.Patient ? patientId : null,
                    flowsheetTemplateScope: state.templateScope,
                    flowsheetTemplateConfiguration: {
                        ...state.careComponentsConfig,
                        CareComponents: [...state.careComponentsConfig.CareComponents.filter(c => c.IsActive)]
                    }
                });
                if (state.isApplyChangesChecked)
                    await onApplySavedFlowsheetTemplateToCurrentFlowsheet();
                onClose();
                actions.setIsLoadingCreation(false);
        }
    }

    const isTemplateNameDuplicateCheckResult = useGetFlowsheetTemplateNameExists(state.templateName, state.templateScope === CreateFlowsheetTemplateScope.Patient);

    function hasActiveComponentWithNoCareElements(): boolean {
        if (!state.careComponentsConfig?.CareComponents) return false;

        return state.careComponentsConfig.CareComponents.some(currentComponent => {
            if (!currentComponent.IsActive) return false;

            const defaultComponent = defaultConfiguration.CareComponents.find(
                def => def.Name === currentComponent.Name
            );

            const defaultHasElements = (defaultComponent?.CareElementsConfig?.length ?? 0) > 0;
            const currentIsEmpty = (currentComponent?.CareElementsConfig?.length ?? 0) === 0;

            return defaultHasElements && currentIsEmpty;
        });
    }

    const isSavingDisabled = state.isLoadingCreation
        || state.careComponentsConfig == null
        || state.careComponentsConfig.CareComponents.length === 0
        || hasActiveComponentWithNoCareElements()
        || state.careComponentsConfig.CareComponents.every(careComponent => !careComponent.IsActive)
        || hasInvalidCareElementConfiguration
        || (state.configurationStep === ConfigureFlowsheetModalStepEnum.TemplateType
        && state.templateScope !== CreateFlowsheetTemplateScope.Patient
        && (isTemplateNameDuplicateCheckResult.isLoading
        || isTemplateNameDuplicateCheckResult.isError
        || isTemplateNameDuplicateCheckResult.isDebouncing
        || isTemplateNameDuplicateCheckResult.data?.IsDuplicateFlowsheetTemplateName
        || state.templateName.length === 0
        || state.templateScope === null
        ));

    useEffect(() => {
        if (state.templateScope === CreateFlowsheetTemplateScope.Patient && isTemplateNameDuplicateCheckResult.data?.SuggestedName) {
            actions.setTemplateName(isTemplateNameDuplicateCheckResult.data?.SuggestedName);
        }
    }, [state.templateScope, isTemplateNameDuplicateCheckResult.data?.SuggestedName, actions]);

    return {
        state: {
            ...state,
            isSavingDisabled,
            isTemplateNameDuplicate: isTemplateNameDuplicateCheckResult.data?.IsDuplicateFlowsheetTemplateName,
            isTemplateNameControlDisabled: state.templateScope === CreateFlowsheetTemplateScope.Patient,
            hasInvalidCareElementConfiguration,
            invalidConfigurationCareElements: invalidSet
        },
        actions: {
            ...actions,
            onCancel,
            onSave
        }
    };
}

export {
    IConfigureFlowsheetResult,
    useConfigureFlowsheet
};
